import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { FieldProps, GenericObjectType, RJSFSchema, StrictRJSFSchema } from '@rjsf/utils';
import Fuse from 'fuse.js';
import Handlebars from 'handlebars';
import { useMemo, useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import { SARNarrativeTemplate } from 'dashboard/models/case-template';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover';
import Spacer from 'design-system/components/Spacer';
import { captureMessage } from 'lib/reporting';

const ListWrapper = styled('div')`
  overflow: auto;
  padding: ${Spacing[2]};
`;

const SearchContainer = styled.div`
  padding: ${Spacing[2]} ${Spacing[2]} 0 ${Spacing[2]};
`;

const PopoverCard = styled(Card)`
  width: 250px;
  height: 300px;
`;

const UnorderedList = styled('ul')`
  list-style-type: none;
`;

const ListItem = styled('li')`
  padding: ${Spacing[2]};
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.primary.brand100};
    border-radius: ${BORDER_RADIUSES.rounded};
  }
`;

const HeaderContainer = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  gap: ${Spacing[2]};
`;

const SAR_NARRATIVE_DESCRIPTION =
  'The narrative helps law enforcement understand why the activity is suspicious.';

/** The `SARNarrativeField` component is used to render a text box for SAR narrative with an button to insert sar narrative template.
 *
 * @param props - The `SARNarrativeField` props for the component
 */
export function SARNarrativeField<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends GenericObjectType = any,
>({
  autofocus,
  disabled,
  formContext,
  formData,
  idSchema,
  label,
  name,
  options = {},
  placeholder,
  readonly,
  registry,
  required,
  schema,
  uiSchema,
  onChange,
  onBlur,
  onFocus,
}: FieldProps<T, S, F>) {
  const id = idSchema.$id;
  const { DescriptionFieldTemplate } = registry.templates;
  const { TextareaWidget } = registry.widgets;
  const sarNarrativeTemplates = formContext?.sarNarrativeTemplates || [];
  const caseDataManager = formContext?.caseDataManager || {};

  Handlebars.registerHelper('jsonStringify', (context: any) => {
    // NOTE: Handlebars doesn't escape the quotes so they show up as &quot so we replace before we render
    //       BETTER IDEA would be appreciated
    return `${JSON.stringify(context, null, 2)?.replace(/"/g, '') ?? ''}`;
  });

  const handleInsertTemplate = (narrativeTemplate: SARNarrativeTemplate) => {
    const resolvedNarrative = interpolateHandlebars(narrativeTemplate.body, caseDataManager);
    const newNarrative = formData ? `${formData}\n${resolvedNarrative}` : resolvedNarrative;

    onChange(newNarrative as any, id);
  };

  return (
    <Card>
      <HeaderContainer>
        <div>
          <DescriptionFieldTemplate
            id={`${id}-description`}
            description={SAR_NARRATIVE_DESCRIPTION}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
          <a
            target='_blank'
            rel='noreferrer'
            href='https://bsaefiling.fincen.treas.gov/docs/XMLUserGuide_FinCENSAR.pdf#page=178'
          >
            Learn more
          </a>
        </div>
        {!readonly && sarNarrativeTemplates.length > 0 && (
          <NarrativeTemplatePopover
            disabled={disabled || false}
            narrativeTemplates={sarNarrativeTemplates}
            onSelectTemplate={handleInsertTemplate}
          />
        )}
      </HeaderContainer>
      <Spacer height={1} />
      <TextareaWidget
        id={id}
        options={options}
        placeholder={placeholder}
        value={formData ? (formData as any) : ''}
        required={required}
        disabled={disabled}
        readonly={readonly}
        autofocus={autofocus}
        name={name}
        schema={schema}
        label={label}
        registry={registry}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    </Card>
  );
}

interface NarrativeTemplatePopoverProps {
  disabled: boolean;
  narrativeTemplates: SARNarrativeTemplate[];
  onSelectTemplate: (template: SARNarrativeTemplate) => void;
}

const NarrativeTemplatePopover = ({
  disabled,
  narrativeTemplates,
  onSelectTemplate,
}: NarrativeTemplatePopoverProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: false,
      threshold: 0.4,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [{ name: 'name', weight: 1.0 }],
    };
    return new Fuse(narrativeTemplates, fuseOptions);
  }, [narrativeTemplates]);

  const filteredNarrativeTemplates = useMemo(
    () =>
      searchTerm !== '' ? fuse.search(searchTerm).map(({ item }) => item) : narrativeTemplates,
    [searchTerm, narrativeTemplates, fuse],
  );

  const handleClickTemplate = (template: SARNarrativeTemplate) => {
    onSelectTemplate(template);
    setIsPopoverOpen(false);
  };

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={(open) => setIsPopoverOpen(open)}
      trigger={
        <Button width='auto' variant='basic' disabled={disabled} prefixIcon={<PlusIcon />}>
          Insert template
        </Button>
      }
      portal={false}
    >
      <Popover.Content side='bottom' align='end'>
        <PopoverCard elevated>
          <SearchContainer>
            <SearchInput
              title={null}
              value={searchTerm}
              handleInput={setSearchTerm}
              size='md'
              placeholder='Search by template name...'
            />
          </SearchContainer>

          <ListWrapper>
            <UnorderedList>
              {filteredNarrativeTemplates.map((template, idx) => (
                <ListItem
                  id={`sar-narrative-templates-list_item-${idx}`}
                  key={template.token}
                  onClick={() => handleClickTemplate(template)}
                >
                  {template.name}
                </ListItem>
              ))}
            </UnorderedList>
          </ListWrapper>
        </PopoverCard>
      </Popover.Content>
    </Popover>
  );
};

function interpolateHandlebars(templateString: string, data: Record<string, any>) {
  try {
    const template = Handlebars.compile(templateString, { noEscape: true });
    return template(data);
  } catch (error) {
    captureMessage('Failed to parse value.', 'warning', {
      error,
      component: 'SAR',
    });
    // NB: If Handlebars has trouble parsing the value, return a blank string. We don't want the page to crash just because one value can't be parsed.
    return '';
  }
}
